import { useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ModalAtom from "../../../../components/atoms/modal/modal";
import TextFieldAtom from "../../../../components/atoms/text-field/text-field";
import TextAreaAtom from "../../../../components/atoms/text-area/text-area";
import { selectProjectDetails } from "../../../../store/slices/projectDetailsSlice";
import { formatCurrencyToLocale, formatValueToLocale } from "../../../../utils/method";
import { DEFAULT_VAT_PERCENTAGE } from "../../../../constants/vat-config";
import CostGroupDropdown from "../../lv/CostGroupDropdown";
import "../cost-tracking.scss";

// Utility to parse localized number string (handles both comma and dot as decimal separator)
function parseLocaleNumber(str) {
    if (typeof str === "number") {
        return str;
    }
    if (!str) {
        return 0;
    }
    // Remove all spaces
    str = String(str).replace(/\s/g, "");
    // If both comma and dot exist, last one is decimal
    const lastDot = str.lastIndexOf(".");
    const lastComma = str.lastIndexOf(",");
    let normalized = str;
    if (lastDot > lastComma) {
        // Dot is decimal
        normalized = str.replace(/,/g, "");
    } else if (lastComma > lastDot) {
        // Comma is decimal
        const parts = str.split(",");
        const decimalPart = parts.pop();
        normalized = parts.join("").replace(/\./g, "") + "." + decimalPart;
    } else {
        // Only one or none
        normalized = str.replace(/,/g, "").replace(/\./g, "");
    }
    const num = parseFloat(normalized);
    return isNaN(num) ? 0 : num;
}

function AddManualEntryModal({ show, handleClose, handleSave, costGroups }) {
    const { t } = useTranslation();
    const projectDetails = useSelector(selectProjectDetails);

    // Get VAT rate from project (default to 19% if not set)
    const vatRate = projectDetails?.vat ?? DEFAULT_VAT_PERCENTAGE;

    const [formData, setFormData] = useState({
        costGroupId: "",
        companyName: "",
        awardAmount: 0,
        supplementAmount: 0,
        riskAmount: 0,
        actualInvoicedAmount: 0,
        measuredNetAmount: 0,
        notes: "",
    });

    const [errors, setErrors] = useState({});

    // Calculate contract sum (net) = Award + Supplement
    const contractSumNet = useMemo(() => {
        return (
            (parseLocaleNumber(formData.awardAmount) || 0) +
            (parseLocaleNumber(formData.supplementAmount) || 0)
        );
    }, [formData.awardAmount, formData.supplementAmount]);

    // Calculate contract sum (gross) with VAT
    const contractSumGross = useMemo(() => {
        return contractSumNet * (1 + vatRate / 100);
    }, [contractSumNet, vatRate]);

    // Calculate forecast completion (incl. Risk) = Contract Sum Net + Risk
    const forecastCompletion = useMemo(() => {
        return contractSumNet + (parseLocaleNumber(formData.riskAmount) || 0);
    }, [contractSumNet, formData.riskAmount]);

    // Calculate measured gross amount
    const measuredGrossAmount = useMemo(() => {
        return (
            (parseLocaleNumber(formData.measuredNetAmount) || 0) *
            (1 + vatRate / 100)
        );
    }, [formData.measuredNetAmount, vatRate]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    const handleCostGroupChange = (e) => {
        const selectedId = e.target.value;

        setFormData((prev) => ({
            ...prev,
            costGroupId: selectedId,
        }));

        // Clear error when user selects a cost group
        if (errors.costGroupId) {
            setErrors((prev) => ({
                ...prev,
                costGroupId: "",
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.costGroupId) {
            newErrors.costGroupId = t(
                "costTracking.manual-entry.error.cost-group-required",
            );
        }

        // Validate all required amount fields (allow 0 values)
        if (formData.awardAmount === undefined || formData.awardAmount === null || formData.awardAmount === "") {
            newErrors.awardAmount = t(
                "costTracking.manual-entry.error.award-required",
            );
        } else if (parseLocaleNumber(formData.awardAmount) < 0) {
            newErrors.awardAmount = t(
                "costTracking.manual-entry.error.award-invalid",
            );
        }

        if (formData.supplementAmount === undefined || formData.supplementAmount === null || formData.supplementAmount === "") {
            newErrors.supplementAmount = t(
                "costTracking.manual-entry.error.supplement-required",
            );
        } else if (parseLocaleNumber(formData.supplementAmount) < 0) {
            newErrors.supplementAmount = t(
                "costTracking.manual-entry.error.supplement-invalid",
            );
        }

        if (formData.riskAmount === undefined || formData.riskAmount === null || formData.riskAmount === "") {
            newErrors.riskAmount = t(
                "costTracking.manual-entry.error.risk-required",
            );
        } else if (parseLocaleNumber(formData.riskAmount) < 0) {
            newErrors.riskAmount = t(
                "costTracking.manual-entry.error.risk-invalid",
            );
        }

        if (formData.actualInvoicedAmount === undefined || formData.actualInvoicedAmount === null || formData.actualInvoicedAmount === "") {
            newErrors.actualInvoicedAmount = t(
                "costTracking.manual-entry.error.actual-invoiced-required",
            );
        } else if (parseLocaleNumber(formData.actualInvoicedAmount) < 0) {
            newErrors.actualInvoicedAmount = t(
                "costTracking.manual-entry.error.actual-invoiced-invalid",
            );
        }

        if (formData.measuredNetAmount === undefined || formData.measuredNetAmount === null || formData.measuredNetAmount === "") {
            newErrors.measuredNetAmount = t(
                "costTracking.manual-entry.error.measured-net-required",
            );
        } else if (parseLocaleNumber(formData.measuredNetAmount) < 0) {
            newErrors.measuredNetAmount = t(
                "costTracking.manual-entry.error.measured-net-invalid",
            );
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            // Convert string values to numbers using parseLocaleNumber
            const dataToSave = {
                costGroupId: formData.costGroupId,
                companyName: formData.companyName || null,
                awardAmount: parseLocaleNumber(formData.awardAmount) || 0,
                supplementAmount: parseLocaleNumber(formData.supplementAmount) || 0,
                riskAmount: parseLocaleNumber(formData.riskAmount) || 0,
                actualInvoicedAmount:
                    parseLocaleNumber(formData.actualInvoicedAmount) || 0,
                measuredNetAmount: parseLocaleNumber(formData.measuredNetAmount) || 0,
                notes: formData.notes,
            };

            handleSave(dataToSave);
            handleModalClose();
        }
    };

    const handleModalClose = () => {
        // Reset form
        setFormData({
            costGroupId: "",
            companyName: "",
            awardAmount: 0,
            supplementAmount: 0,
            riskAmount: 0,
            actualInvoicedAmount: 0,
            measuredNetAmount: 0,
            notes: "",
        });
        setErrors({});
        handleClose();
    };

    const modalBody = (
        <div className="add-manual-entry-form">
            {/* VAT Info Badge */}
            <div className="mb-3 d-flex justify-content-end">
                <span className="badge bg-info text-dark">
                    {t("costTracking.manual-entry.vat-rate")}: {vatRate}%
                </span>
            </div>

            <Row className="mb-3">
                {/* Cost Group Selection */}
                <Col xs={6}>
                    <CostGroupDropdown
                        costGroups={costGroups}
                        value={formData.costGroupId}
                        onChange={handleCostGroupChange}
                        isClearable={true}
                        placeholder={t("costTracking.manual-entry.select-cost-group")}
                        label={t("costTracking.manual-entry.cost-group")}
                        required
                        error={errors.costGroupId}
                        hideIgnoreCategory
                        size="medium"
                    />
                </Col>
                {/* Company Name */}
                <Col xs={6}>
                    <TextFieldAtom
                        label={t("costTracking.manual-entry.company-name")}
                        placeholder={t("costTracking.manual-entry.company-name-placeholder")}
                        value={formData.companyName}
                        onChange={(e) => handleInputChange("companyName", e.target.value)}
                    />
                </Col>
            </Row>

            {/* Award and Supplement */}
            <Row className="mb-2">
                <Col md={4}>
                    <TextFieldAtom
                        label={t("costTracking.manual-entry.award")}
                        placeholder="0,00"
                        value={formData.awardAmount}
                        onChange={(e) =>
                            handleInputChange("awardAmount", e.target.value)
                        }
                        step="0.01"
                        min="0"
                        suffix="€"
                        className="text-end"
                        decimalPlaces={2}
                        required
                        error={errors.awardAmount}
                    />
                </Col>
                <Col md={4}>
                    <TextFieldAtom
                        label={t("costTracking.manual-entry.supplement")}
                        placeholder="0,00"
                        value={formData.supplementAmount}
                        onChange={(e) =>
                            handleInputChange(
                                "supplementAmount",
                                e.target.value,
                            )
                        }
                        step="0.01"
                        min="0"
                        suffix="€"
                        className="text-end"
                        decimalPlaces={2}
                        required
                        error={errors.supplementAmount}
                    />
                </Col>
                <Col xs={4}>
                    <TextFieldAtom
                        label={
                            <b>{t("costTracking.manual-entry.contract-sum-net")}</b>
                        }
                        value={formatValueToLocale(contractSumNet)}
                        disabled
                        className="ct-contract-sum"
                        suffix="€"
                        hint={<>
                            {t("costTracking.manual-entry.contract-sum-gross")}:{" "}
                            {formatCurrencyToLocale(contractSumGross)}
                        </>}
                    />
                </Col>
            </Row>

            {/* Risk */}
            <Row className="mb-2">
                <Col md={4}>
                    <TextFieldAtom
                        label={t("costTracking.manual-entry.risk")}
                        placeholder="0,00"
                        value={formData.riskAmount}
                        onChange={(e) =>
                            handleInputChange("riskAmount", e.target.value)
                        }
                        step="0.01"
                        min="0"
                        suffix="€"
                        className="text-end"
                        decimalPlaces={2}
                        hint={<>{t("costTracking.manual-entry.forecast-completion")}: {formatCurrencyToLocale(forecastCompletion)}</>}
                        required
                        error={errors.riskAmount}
                    />
                </Col>
                <Col md={4}>
                    <TextFieldAtom
                        label={t("costTracking.manual-entry.actual-invoiced")}
                        type="number"
                        placeholder="0,00"
                        value={formData.actualInvoicedAmount}
                        onChange={(e) =>
                            handleInputChange(
                                "actualInvoicedAmount",
                                e.target.value,
                            )
                        }
                        step="0.01"
                        min="0"
                        suffix="€"
                        className="text-end"
                        decimalPlaces={2}
                        required
                        error={errors.actualInvoicedAmount}
                    />
                </Col>
                <Col md={4}>
                    <TextFieldAtom
                        label={t("costTracking.manual-entry.measured-net")}
                        type="number"
                        placeholder="0,00"
                        value={formData.measuredNetAmount}
                        onChange={(e) =>
                            handleInputChange(
                                "measuredNetAmount",
                                e.target.value,
                            )
                        }
                        step="0.01"
                        min="0"
                        suffix="€"
                        className="text-end"
                        decimalPlaces={2}
                        hint={
                            <>
                                {t("costTracking.manual-entry.measured-gross")}:{" "}
                                {formatCurrencyToLocale(measuredGrossAmount)}
                            </>
                        }
                        required
                        error={errors.measuredNetAmount}
                    />
                </Col>
            </Row>

            {/* Notes */}
            <Row className="mb-3">
                <Col xs={12}>
                    <TextAreaAtom
                        label={t("costTracking.manual-entry.notes")}
                        rows={3}
                        placeholder={t("costTracking.manual-entry.notes-placeholder")}
                        value={formData.notes}
                        onChange={(e) => handleInputChange("notes", e.target.value)}
                    />
                </Col>
            </Row>
        </div >
    );

    return (
        <ModalAtom
            show={show}
            handleClose={handleModalClose}
            handleSubmit={handleSubmit}
            title={t("costTracking.manual-entry.title")}
            body={modalBody}
            saveText="costTracking.manual-entry.save"
            centered={true}
            size="lg"
        />
    );
}

export default AddManualEntryModal;
